import { useEffect, useMemo, useState } from 'react';

import CustomGrid from '../CustomGrid';
import { gridStructure } from 'constants/CustomGridStructure';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { Box, Paper } from '@mui/material';
import Loader from '../Loader';

import AddUnitModal from './AddUnitModal';
import EditUnitModal from './EditUnitModal';
import DeleteConfirmationModaL from 'components/utils/DeleteConfirmationModal';
import { EnhancedTableToolbar } from '../EnhancedToolbar';

import { Unit } from 'types';
import { UNITS_URL as BASE_URL } from 'constants/urls';
import { ENTITY } from 'constants/entities';

import useModals from '../../../hooks/useModals';
import useCrud from '../../../hooks/useCrud';
import { useSession } from 'providers/SessionProvider';
import { useToast } from 'hooks/useToast';
import { STATUS_CODES } from 'constants/index';
import { useFilter } from 'hooks/useFilter';
import { getUserFullName } from 'helpers';
import { exportToExcel } from 'helpers/exportToExcel';

export default function UnitsList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const { units, addSessionData, updateSessionData, deleteSessionData, users } =
    useSession();

  const { showSnackBar } = useToast();

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();
  const {
    loading,
    firstLoad,
    createData: createUnit,
    updateData: updateUnit,
    deleteData: deleteUnit,
    setShowToast,
    showToast,
    toastMessage,
    toastSeverity,
  } = useCrud<Unit>(BASE_URL);

  const enrichedUnits = useMemo(() => {
    return units.map((item) => ({
      ...item,
      created_by_name: (item as Unit & { created_by?: number | null }).created_by
        ? getUserFullName(
            (item as Unit & { created_by?: number | null }).created_by,
            users,
          )
        : 'SISTEMA',
    }));
  }, [units, users]);

  const { filterText, setFilterText, filteredData: filteredUnits } = useFilter<Unit & { created_by_name: string }>({
    data: enrichedUnits,
    fields: gridStructure[ENTITY.UNITS].fields,
  });

  const unitsExcelColumns = {
    "Descripción": (u: Unit) => u.description ?? "-",
    "Activo": (u: Unit) => (u.is_active ? "Sí" : "No"),
    "Creado por": (u: Unit & { created_by_name?: string }) =>
      u.created_by_name ?? "-",
  } satisfies Record<string, (row: Unit) => any>;

  const handleExportUnitsExcel = () => {
    exportToExcel<Unit>({
      data: filteredUnits,
      columns: unitsExcelColumns,
      sheetName: "Unidades",
      fileNamePrefix: "Unidades",
      headerRows: [
        ["Listado de Unidades"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const handleCreate = async (newUnit: Unit) => {
    const response = await createUnit(newUnit, true, true);
    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.UNITS, response.data as Unit);
    }
  };

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteUnit(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.UNITS,
      selected
        .filter(item => !failedDeletes.includes(Number(item)))
        .map(item => item.toString())
    );

    setSelected([]);
  };

  const handleEdit = async (unitToUpdate: Unit) => {
    const response = await updateUnit(
      unitToUpdate.id,
      unitToUpdate,
      true,
      true
    );
    if (response.status === STATUS_CODES.OK) {
      updateSessionData(ENTITY.UNITS, unitToUpdate);
    }

    setSelected([]);
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Loader isFirstLoad={firstLoad} isLoading={loading} />
          <AddUnitModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.UNITS}
            values={selected.map(item => {
              const value = units.find(units => units.id === item)?.description;
              if (value) return value;
              return '';
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditUnitModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={units.find(units => units.id === selected[0])}
              onEdit={handleEdit}
            />
          ) : null}
          <Box sx={{ width: '99%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportUnitsExcel}
              />
              <CustomGrid<Unit>
                structure={gridStructure[ENTITY.UNITS]}
                rows={filteredUnits}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
